import type { IntlShape } from 'react-intl'
import { prettyFlexibleDates } from 'components/Search/SearchForm/SearchDateInput/FlexibleDateSearchField'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import type { DrawerHotel } from '../../components/HotelListingDrawer/types'
import type { ListingDrawerHotelDateSelection } from '../../components/ListingDrawerSearchDates'
import { compactLines } from './message'

const MAX_HOTEL_SEARCH_RESULTS_IN_CONTEXT = 3

interface BuildHotelSearchDrawerContextArgs {
  isLoading: boolean
  hasHotels: boolean
  searchLocation: string
  occupancies: Array<App.Occupants>
  savedDrawerHotels: Array<DrawerHotel>
  orderedHotelsWithTileDetails: Array<DrawerHotel>
  hotelDateSelection?: ListingDrawerHotelDateSelection
  intl: IntlShape
  checkIn?: string
  checkOut?: string
  flexibleMonths?: string
  flexibleNights?: string
}

function formatHotelSearchResult(hotel: DrawerHotel, index: number): string {
  const price = hotel.price?.currency
    ? `, ${hotel.price.currency} ${hotel.price.total} for ${hotel.price.nights} night${hotel.price.nights !== 1 ? 's' : ''}`
    : ''
  const location = hotel.location ? `, ${hotel.location}` : ''
  return `Result ${index + 1}: ${hotel.name}${location}${price}`
}

function formatHotelSearchRooms(rooms: Array<App.Occupants>): string {
  return rooms
    .map((room, index) => `Room ${index + 1}: ${formatOccupantsShort(room)}`)
    .join('; ')
}

export function buildHotelSearchDrawerContext({
  isLoading,
  hasHotels,
  searchLocation,
  checkIn,
  checkOut,
  flexibleMonths,
  flexibleNights,
  hotelDateSelection,
  intl,
  occupancies,
  savedDrawerHotels,
  orderedHotelsWithTileDetails,
}: BuildHotelSearchDrawerContextArgs): string {
  return compactLines([
    'Drawer: Hotel search',
    `Status: ${isLoading ? 'searching' : hasHotels ? 'searched' : 'no results'}`,
    `Destination: ${searchLocation}`,
    checkIn && `Check-in: ${checkIn}`,
    checkOut && `Check-out: ${checkOut}`,
    hotelDateSelection?.type === 'flexible'
      ? `Flexible dates: ${prettyFlexibleDates(
          flexibleMonths,
          flexibleNights,
          undefined,
          intl,
        )}`
      : undefined,
    hotelDateSelection?.type === 'anytime' ? 'Dates: Anytime' : undefined,
    `Guests: ${formatHotelSearchRooms(occupancies)}`,
    `Saved results: ${savedDrawerHotels.length}`,
    `Explore results visible: ${orderedHotelsWithTileDetails.length}`,
    orderedHotelsWithTileDetails.length > 0 && 'Top visible results:',
    ...orderedHotelsWithTileDetails
      .slice(0, MAX_HOTEL_SEARCH_RESULTS_IN_CONTEXT)
      .map(formatHotelSearchResult),
  ])
}
